<template>
  <div class="mb-10 flex size-full flex-col items-center md:max-w-7xl md:px-5 lg:px-10">
    <div class="mb-3 flex w-full items-center px-2 md:px-0">
      <p class="flex flex-col self-end text-xl text-gray-700 md:text-2xl lg:text-3xl">
        <span>Descuento de productos</span>
        <a
          :href="`/makers/${maker.slug}`"
          class="mt-1 text-sm font-light hover:underline md:text-base"
        >
          Revisar descuentos
        </a>
      </p>
      <button
        class="ml-auto flex items-center rounded-md border border-lk-green bg-white p-2 text-sm text-lk-green hover:bg-lk-green hover:text-white md:p-3 md:text-base"
        @click="(showDiscountNewDialog = !showDiscountNewDialog)"
      >
        <PlusIcon class="mr-2 size-5 shrink-0" />
        <span>Agregar descuento</span>
      </button>
    </div>
    <div class="flex size-full flex-col rounded-md bg-white px-2 py-3 text-gray-700 md:px-4 md:py-5 lg:px-10">
      <template v-if="!selectedDiscount">
        <div class="mb-5 flex w-full items-center self-start text-sm md:text-base">
          <template
            v-for="(value, key) in statusHash"
            :key="key"
          >
            <button
              class="shrink-0 border-b-2 py-3"
              :class="{ 'border-gray-300': selectedStatus !== key, 'border-lk-green': selectedStatus === key }"
              @click="updateSelectedStatus(key)"
            >
              {{ value }}
            </button>
            <div class="w-10 self-end border-b-2 border-gray-300 last:w-full" />
          </template>
        </div>
        <lokal-loading
          v-if="discountsIsLoading"
          :loading="discountsIsLoading"
        />
        <template v-else>
          <div
            v-if="paginationInfo"
            class="mb-4 text-sm text-gray-500 md:text-base"
          >
            {{ paginationInfo }}
          </div>
          <p
            v-if="!discounts || discounts.length === 0"
            class="text-sm md:text-base"
          >
            No hay descuentos
          </p>
          <div v-else>
            <makers-discount-list-card
              v-for="discount in discounts"
              :key="discount.id"
              :discount="discount"
              @view-products="handleViewProducts"
            />
            <pagination-component
              v-if="totalPages && totalPages > 1"
              :total-pages="totalPages"
              :current-page="currentPage"
              @set-new-page="handlePageChange"
            />
          </div>
        </template>
      </template>
      <makers-discount-show
        v-if="selectedDiscount"
        :discount="selectedDiscount"
        @go-back="handleGoBack"
      />
    </div>
    <makers-discount-new-dialog
      v-if="showDiscountNewDialog"
      v-model="showDiscountNewDialog"
      :maker-id="maker.id"
      @reload-discounts="async () => await discountsQuery.refetch.value()"
      @discount-created="handleDiscountCreated"
    />
    <makers-discount-add-products-dialog
      v-if="showAddProductsDialog"
      v-model="showAddProductsDialog"
      :discount="createdDiscount!"
      :maker-id="maker.id"
      @reload-discounts="async () => await discountsQuery.refetch.value()"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { PlusIcon } from '@heroicons/vue/24/outline';
import MakersDiscountListCard from './makers-discount-list-card.vue';
import MakersDiscountShow from './makers-discount-show.vue';
import PaginationComponent from './shared/pagination-component.vue';
import MakersDiscountNewDialog from './makers-discount-new-dialog.vue';
import MakersDiscountAddProductsDialog from './makers-discount-add-products-dialog.vue';
import useDiscountsQuery from './queries/discounts-query';

const props = defineProps<{
  maker: Maker;
}>();

const STATUSES_HASH = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
} as const;

const PER_PAGE = 10;

const selectedStatus = ref<keyof typeof STATUSES_HASH>('all');
const statusHash = ref({ all: 'Todos', active: 'Activos', inactive: 'Vencidos' });
const selectedDiscount = ref<Discount | null>(null);
const currentPage = ref(1);
const showDiscountNewDialog = ref(false);
const showAddProductsDialog = ref(false);
const createdDiscount = ref<Discount | null>(null);

function updateSelectedStatus(status: keyof typeof STATUSES_HASH) {
  selectedStatus.value = status;
  currentPage.value = 1;
}

function handlePageChange(page: number) {
  currentPage.value = page;
}

function handleViewProducts(discount: Discount) {
  selectedDiscount.value = discount;
}

function handleGoBack() {
  selectedDiscount.value = null;
}

function handleDiscountCreated(discount: Discount) {
  createdDiscount.value = discount;
  showAddProductsDialog.value = true;
}

const discountQueryParams = computed(() => {
  const currentTimestamp = new Date().toISOString();
  if (selectedStatus.value === 'active') return `q[end_date_gt]=${currentTimestamp}`;
  if (selectedStatus.value === 'inactive') return `q[end_date_lt]=${currentTimestamp}`;
  return '';
});

const paginationInfo = computed(() => {
  if (!totalResources.value || totalResources.value === 0) {
    return null;
  }
  const startItem = (currentPage.value - 1) * resourcesPerPage.value + 1;
  const endItem = Math.min(currentPage.value * resourcesPerPage.value, totalResources.value);

  return `Mostrando ${startItem} - ${endItem} de ${totalResources.value} descuentos`;
});

const { discountsIsLoading, discounts, totalResources, resourcesPerPage, totalPages, discountsQuery } =
  useDiscountsQuery(
    computed(() => props.maker.id),
    discountQueryParams,
    computed(() => currentPage.value),
    computed(() => PER_PAGE),
  );

watch(
  totalPages,
  (newTotalPages) => {
    if (newTotalPages && currentPage.value > newTotalPages && newTotalPages > 0) {
      currentPage.value = newTotalPages;
    }
  },
  { immediate: true },
);
</script>
